import { api, track, wire, LightningElement, WireConfigValue } from "lwc";
import { getEmitter } from "@devforce/trailhead-client-events";
import { logger } from "@devforce/trailhead-client-logger";
import {
  EarnedStampsQuery,
  LockedStampsQuery,
} from "@devforce/trailhead-client-gql";
import { QueryAdapter, QueryAdapterValue } from "common/graphqlAdapter";

import type {
  StampLockedConnectionType,
  StampEarnedConnectionType,
  StampLockedType,
  StampEarnedType,
} from "@devforce/trailhead-client-gql";
import type { State as LoadingState } from "tds/contentCollectionLoadingState";
import type { CollectionMode, CollectionVariant } from "tds/stampCollection";

enum QueryTasks {
  InitialLocked,
  AdditionalLocked,
  InitialEarned,
  AdditionalEarned,
}

const PAGE_SIZE: number = 8;

export default class StampCollection extends LightningElement {
  @api tbid!: string;
  @api variant: CollectionVariant = "default";
  @track stamps: (StampLockedType | StampEarnedType)[] = [];
  private currentTask: QueryTasks = QueryTasks.InitialEarned;
  private totalStamps: number | 0 = 0;
  private queryCursor: string | null = null;
  private moreStampsRequested = false;
  private queryError = false;
  private queryLoading = true;
  private oneTimeRedirectUsed = false;

  connectedCallback(): void {
    getEmitter().on("custom", (e) => {
      switch (e.name) {
        case "tds-stamp-collection:load-more":
          this.handleLoadMore();
          break;
        case "tds-stamp-collection:load-locked":
          this.handleLoadLockedStamps();
          break;
        case "tds-stamp-collection:load-earned":
          this.handleLoadEarnedStamps();
          break;
        default:
      }
    });
  }

  disconnectedCallback(): void {
    getEmitter().off("custom");
  }

  /**
   * Wire Query
   * ----------
   * Uses our custom `QueryAdapter` Wire Adapter to fetch the data from the GraphQL server.
   * Which query and variables are used are controlled by the `queryOptions()` getter.
   **/

  @wire(QueryAdapter, { options: "$queryOptions" })
  onStampsLoaded(
    result: QueryAdapterValue<{
      earnedStamps?: StampEarnedConnectionType;
      lockedStamps?: StampLockedConnectionType;
    }>
  ) {
    try {
      this.validateAttributes();

      if (result.error) throw new Error(`[query] - ${result.error}`);

      let stampsData;

      if (
        this.currentTask == QueryTasks.InitialEarned ||
        this.currentTask == QueryTasks.AdditionalEarned
      ) {
        stampsData = result.data.earnedStamps;
      } else {
        stampsData = result.data.lockedStamps;
      }

      if (stampsData) {
        this.totalStamps = stampsData.totalCount || 0;
        this.queryCursor = stampsData.pageInfo.endCursor;
        const edges = stampsData.edges || [];

        edges.forEach((edge) => {
          this.stamps.push(edge!.node!);
        });
      }

      if (
        this.stamps.length == 0 &&
        !this.oneTimeRedirectUsed &&
        this.currentTask == QueryTasks.InitialEarned
      ) {
        this.oneTimeRedirectUsed = true;
        this.currentTask = QueryTasks.InitialLocked;
        return;
      }

      if (
        this.currentTask == QueryTasks.InitialEarned ||
        this.currentTask == QueryTasks.InitialLocked
      ) {
        getEmitter().emit("custom", {
          name: "th-stamp-collection:initial-load-completed",
          payload: null,
        });
      } else {
        getEmitter().emit("custom", {
          name: "th-stamp-collection:load-more-completed",
          payload: null,
        });
      }

      this.queryLoading = false;
    } catch (e: unknown) {
      if (e instanceof Error) {
        logger.error(`[th-stamp-collection][error]${e.message}`);
      }
      this.queryError = true;
      this.queryLoading = false;
    }
  }

  /**
   * Computed Wire Adapter Properties
   * --------------------------------
   * Uses `LockedStampsQuery` or `EarnedStampsQuery`, depending on the
   * `currentTask` value. It also uses the `queryCursor` value to fetch
   * additional stamps.
   */
  private get queryOptions() {
    let query;
    const initialLoadTasks = [
      QueryTasks.InitialLocked,
      QueryTasks.InitialEarned,
    ];
    const additionalLoadTasks = [
      QueryTasks.AdditionalLocked,
      QueryTasks.AdditionalEarned,
    ];

    if (
      this.currentTask == QueryTasks.InitialLocked ||
      this.currentTask == QueryTasks.AdditionalLocked
    ) {
      query = LockedStampsQuery;
    } else {
      query = EarnedStampsQuery;
    }

    let options: WireConfigValue | null = null;
    if (initialLoadTasks.includes(this.currentTask)) {
      options = {
        query: query,
        variables: {
          trailblazerId: this.tbid,
          first: PAGE_SIZE,
        },
      };
    } else if (
      additionalLoadTasks.includes(this.currentTask) &&
      this.moreStampsRequested
    ) {
      options = {
        query: query,
        variables: {
          trailblazerId: this.tbid,
          first: PAGE_SIZE,
          after: this.queryCursor,
        },
      };
      this.moreStampsRequested = false;
    }
    return options;
  }

  /**
   * Local helper methods
   */

  private validateAttributes() {
    if (!this.tbid || this.tbid.length == 0) {
      throw new Error('[params] - attribute "tbid" is required.');
    }
  }

  /**
   * Event Handlers
   */

  private handleLoadEarnedStamps() {
    this.clearStamps();
    this.currentTask = QueryTasks.InitialEarned;
  }

  private handleLoadLockedStamps() {
    this.clearStamps();
    this.currentTask = QueryTasks.InitialLocked;
  }

  private handleLoadMore() {
    if (this.currentTask == QueryTasks.InitialLocked) {
      this.currentTask = QueryTasks.AdditionalLocked;
    } else if (this.currentTask == QueryTasks.InitialEarned) {
      this.currentTask = QueryTasks.AdditionalEarned;
    }
    this.moreStampsRequested = true;
  }

  private clearStamps() {
    this.stamps = [];
    this.totalStamps = 0;
    this.queryCursor = null;
    this.queryError = false;
  }

  /**
   * Template-Only Methods
   * ---------------------
   * These methods are used in the template only and are not called from the
   * component's JavaScript code.
   * ---------------------
   * */

  private get loadingState(): LoadingState {
    if (this.queryError) return "error";
    if (this.queryLoading) return "loading";
    if (this.stamps) return "loaded";
    return "empty";
  }

  private get collectionMode(): CollectionMode {
    switch (this.currentTask) {
      case QueryTasks.InitialLocked:
      case QueryTasks.AdditionalLocked:
        return "locked";
      case QueryTasks.InitialEarned:
      case QueryTasks.AdditionalEarned:
      default:
        return "unlocked";
    }
  }

  private get totalStampsCount(): number | 0 {
    return this.totalStamps;
  }

  private get collectionVariant(): CollectionVariant {
    return this.variant;
  }
}
